/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnInit, ViewChild } from '@angular/core';
import { DecksService, DeckWithCardSizes } from '../decks.service';
import { CardService } from '../../cards/card.service';
import { BehaviorSubject, combineLatest, merge, Observable, of } from 'rxjs';
import { debounceTime, filter, flatMap, map, switchMap } from 'rxjs/operators';
import { Utils } from '../common/utils';
import { Block, BlockData, Card, Deck, InventoryCard, Query } from '@deckplan.io/common';
import { Format as DeckFormat, formats } from '@deckplan.io/common/decks/format';
import { CardTableComponent, PageChanges } from '../../cards/card-table/card-table.component';
import { CommonColumns } from '../common/common-columns';
import { ContextMenuComponent } from '../../common/context-menu/context-menu.component';
import { ActivatedRoute } from '@angular/router';
import { AddFocusEvent, Format } from '../formats/format.interface';
import { MatDialog } from '@angular/material/dialog';
import { FormatFactory } from '../formats/format-factory';
import { CardAndRelation } from '@deckplan.io/common/decks/deck';
import { FormControl, Validators } from '@angular/forms';
import { blocks } from '@deckplan.io/common/decks/block';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Gtag } from 'angular-gtag';
import { AuthService } from '../../auth/auth.service';
import { CardSearchComponent } from '../../cards/card-search/card-search.component';
import { Clipboard } from '@angular/cdk/clipboard';
import { AlertLevel, AlertsService } from '../../common/alerts/alerts.service';
import { User } from '@deckplan.io/common/users';
import { Theme, ThemeService } from '../../common/theme/theme.service';
import { UsersService } from '../../users/users.service';
import { ManualImporterComponent } from '../../cards/manual-importer/manual-importer.component';
import { InventoryService } from '../../inventory/inventory.service';
import { EditableDataField } from '../../common/filter/editable-data-field';
import { FileImporterComponent } from '../../cards/file-importer/file-importer.component';
import { InventoryCardDialogComponent, InventoryCardDialogData } from '../../inventory/inventory-card-dialog/inventory-card-dialog.component';

@Component({
    selector: 'app-deck-view',
    templateUrl: './deck-view.component.html',
    styleUrls: ['./deck-view.component.scss']
})
export class DeckViewComponent implements OnInit {

    public deck: DeckWithCardSizes;
    public deckNameCtrl: FormControl;
    public deckFormatCtrl: FormControl;
    public blockCtrl: FormControl;
    public searchRulesCtrl: FormControl;
    public isPublicCtrl: FormControl;
    public isLinkShareableCtrl: FormControl;
    public focusedCard = new BehaviorSubject<Card>(null);
    public addQuery: Query = null;

    public blocks = blocks;

    public fullEditMode = false;
    public editName = false;
    public editFormat = false;
    public editFocus = false;
    public showSummary = true;

    public readonly formats = formats;

    public queryUpdated = new BehaviorSubject<Query>(null);
    public pageUpdated = new BehaviorSubject<PageChanges>(null);

    public readonly columns = [
        {selected: true, field: {title: 'Quantity', field: 'quantityInDeck', dataType: 'number', isInput: true}},
        {selected: true, field: {title: 'Owned', field: 'inventoryId', dataType: 'boolean', isInput: true}},
        ...CommonColumns.slice(1)
    ];

    public readonly searchColumns = CommonColumns;

    public typeCompositions: Observable<any>;
    public colorStats: Observable<any[]>;
    public cmcHisto: Observable<any>;
    public manaBalance: Observable<any>;

    public format: Format = null;
    public invalidCards = new Set<string>();

    public currentUser: User;
    public likeButtonColor = 'white';
    public likeIconName = 'favorite_border';

    @ViewChild(ContextMenuComponent, {static: false}) private readonly contextMenu: ContextMenuComponent;
    @ViewChild(CardTableComponent, {static: false}) private readonly cardTable: CardTableComponent;
    @ViewChild('cardSearch', {static: false}) private readonly cardSearch: CardSearchComponent;

    private refreshCharts = true;
    private disableSearchRules = false;

    constructor(private readonly decksService: DecksService,
                private readonly cardsService: CardService,
                private readonly route: ActivatedRoute,
                private readonly gtag: Gtag,
                private readonly inventoryService: InventoryService,
                private readonly alertsService: AlertsService,
                public readonly authService: AuthService,
                private readonly dialog: MatDialog,
                private readonly clipboard: Clipboard,
                private readonly themeService: ThemeService,
                private readonly usersService: UsersService) { }

    public cardValidator(card: Card): boolean {
        return !this.invalidCards.has(card.name);
    }

    private deckQuery(query: Query): Query {
        if (!query && !this.format) {
            return null;
        } else if (!query) {
            return this.format.deckCardsQuery();
        } else if (!this.format) {
            return query;
        } else {
            return {
                operator: 'and',
                operands: [
                    query,
                    this.format.deckCardsQuery()
                ]
            };
        }
    }

    public ngOnInit(): void {
        this.deckNameCtrl = new FormControl();
        this.deckFormatCtrl = new FormControl();
        this.blockCtrl = new FormControl();
        this.searchRulesCtrl = new FormControl();
        this.isPublicCtrl = new FormControl();
        this.isLinkShareableCtrl = new FormControl();

        this.updateCurrentUser();

        this.deckNameCtrl.valueChanges.pipe(
            debounceTime(200),
            filter(newName => newName === this.deck.name)
        ).subscribe(newName => this.deck.name = newName);
        this.deckFormatCtrl.valueChanges.subscribe(this.handleFormatChange.bind(this));
        this.blockCtrl.valueChanges.subscribe(this.handleBlockChange.bind(this));
        this.searchRulesCtrl.valueChanges.subscribe(this.handleSearchRulesChecked.bind(this));

        combineLatest([
            this.queryUpdated,
            this.pageUpdated.pipe(flatMap(pageChange => of(Utils.PageOrDefault(pageChange)))),
            this.route.params.pipe(filter(paramMap => paramMap.id))
        ]
        ).pipe(flatMap(results => {
            const query = results[0];
            Utils.TranslateOwnedFieldFilters(query);
            const pagination = results[1];
            // eslint-disable-next-line no-magic-numbers
            const paramMap = results[2];
            return this.decksService.get(paramMap.id, this.deckQuery(query), pagination);
        })).subscribe(this.handleDeckUpdate.bind(this));

        this.themeService.themeChanges.subscribe(theme => {
            if (theme === Theme.Dark) {
                this.likeButtonColor = 'white';
            } else {
                this.likeButtonColor = 'accent';
            }
        });
    }

    private updateCurrentUser() {
        if (this.authService.currentUserId === 'anonymous') {
            this.currentUser = undefined;
        } else {
            this.usersService.getOne(this.authService.currentUserId).subscribe((user) => this.currentUser = user);
        }
    }

    private handleFormatChange(newDeckFormat: DeckFormat) {
        // TODO: Validate new format and warn user of need to remove invalid cards
        if (this.deck) {
            this.deck.format = newDeckFormat;
            this.format = FormatFactory.create(this.deck, this.cardsService, this.dialog, false);
            this.setAddCardsQuery();
        }
    }

    private handleBlockChange(newBlock: Block) {
        // TODO: Validate new format and warn user of need to remove invalid cards
        if (this.deck) {
            this.deck.specialData = {block: newBlock, focusedId: this.focusedCard.value ? this.focusedCard.value.id : ''} as BlockData;
            this.format = FormatFactory.create(this.deck, this.cardsService, this.dialog, false);
            this.setAddCardsQuery();
        }
    }

    private handleSearchRulesChecked(disableSearchRules: boolean) {
        this.disableSearchRules = disableSearchRules;
        this.setAddCardsQuery();
    }

    // Sets the addQuery to the current format's query, or to undefined if the disableSearchRules value has
    // been set
    private setAddCardsQuery() {
        if (!this.disableSearchRules) {
            this.addQuery = this.format.addCardsQuery();
        } else {
            this.addQuery = undefined;
        }

        setTimeout(() => {
            // If we are updating our deck, then we
            // most likely have need to refresh our add card query
            this.cardSearch.forceRequery.next(1);
            // eslint-disable-next-line no-magic-numbers
        }, 100);
    }

    private handleDeckUpdate(deck: DeckWithCardSizes) {
        // Update our form controls to have the right values
        this.deckNameCtrl.setValue(deck.name);
        this.deckFormatCtrl.setValue(deck.format);
        this.deckFormatCtrl.setValidators(Validators.pattern(deck.format));
        this.isPublicCtrl.setValue(deck.isPublic);
        this.isLinkShareableCtrl.setValue(deck.isLinkShareable);

        // Set our deck
        this.deck = deck;

        this.setLikeIconName();

        if (!this.format) {
            // Get the format rules for our deck type
            this.decksService.get(deck.id).subscribe(fullDeck => {
                this.format = FormatFactory.create(fullDeck, this.cardsService, this.dialog, false);
                // Get the focused card if any
                this.format.getFocused().subscribe(card => this.focusedCard.next(card as Card));

                // Get the "Add Cards" setQuery if any
                this.setAddCardsQuery();

                this.invalidCards = new Set(this.format.validate().map(card => card.name));

                this.forceUpdate();
            });
        }

        if (this.refreshCharts) {
            this.refreshCharts = false;

            // Get our statistics data
            this.typeCompositions = this.decksService.typeCompositions(this.deck.id);
            this.colorStats = this.decksService.colorStats(this.deck.id).pipe(map(stats => [stats.W, stats.U, stats.B, stats.R, stats.G, stats.C]));
            this.cmcHisto = this.decksService.cmcStats(this.deck.id).pipe(map(stats => {
                // The range for cmc is 0-16, so our histogram needs that many slots
                // eslint-disable-next-line no-magic-numbers
                const cmcHisto = [...Array(17).keys()].reduce((o, c) => {
                    o[c] = 0;
                    return o;
                }, {});
                for (const cmc of stats.cmc) {
                    if (cmcHisto.hasOwnProperty(cmc)) {
                        cmcHisto[cmc] += 1;
                    } else {
                        cmcHisto[cmc] = 1;
                    }
                }
                return cmcHisto;
            }));
            // 'Red', 'Blue', 'Black', 'Green', 'White', 'Colorless'
            this.manaBalance = this.decksService.cmcStats(this.deck.id).pipe(map(stats => [
                {
                    label: 'Consumers',
                    data: [stats.sink.R, stats.sink.U, stats.sink.B, stats.sink.G, stats.sink.W, stats.sink.C],
                    color: '#0000ffbb'
                },
                {
                    label: 'Producers',
                    data: [stats.source.R, stats.source.U, stats.source.B, stats.source.G, stats.source.W, stats.source.C],
                    color: '#ff0000'
                }
            ]));
        }
    }

    public addCard(card: (Card | InventoryCard) & { quantityInDeck: number }) {
        let addObservable;
        if ((card as InventoryCard).inventoryId) {
            addObservable = this.decksService.addInventoryCard(this.deck.id, (card as InventoryCard).inventoryId, card.quantityInDeck);
        } else {
            addObservable = this.decksService.addCard(this.deck.id, card.id, card.quantityInDeck);
        }

        addObservable.pipe(flatMap((deck: Deck) => {
            this.refreshCharts = true;
            this.gtag.event('card_added_to_deck', {
                card_id: card.id,
                user_id: this.authService.currentUserId,
                deck_id: deck.id,
                deck_format: deck.format,
                event_category: 'decks'
            });
            return this.decksService.get(deck.id, this.deckQuery(this.queryUpdated.value), Utils.PageOrDefault(this.pageUpdated.value));
        }))
            .subscribe(this.handleDeckUpdate.bind(this));
    }

    public removeCard(card: CardAndRelation) {
        this.contextMenu.closeCardContext();
        this.decksService.removeCard(this.deck.id, card.relationToDeckId)
            .pipe(flatMap((deck: Deck) => {
                this.refreshCharts = true;
                this.gtag.event('card_removed_from_deck', {
                    card_id: card.id,
                    user_id: this.authService.currentUserId,
                    deck_id: deck.id,
                    deck_format: deck.format,
                    event_category: 'decks'
                });
                return this.decksService.get(deck.id, this.deckQuery(this.queryUpdated.value), Utils.PageOrDefault(this.pageUpdated.value));
            }))
            .subscribe(this.handleDeckUpdate.bind(this));
    }

    public cancel() {
        this.fullEditMode = false;
        this.decksService.get(this.deck.id, this.deckQuery(this.queryUpdated.value), Utils.PageOrDefault(this.pageUpdated.value))
            .subscribe(this.handleDeckUpdate.bind(this));
    }

    public saveName() {
        this.editName = false;
        this.deck.name = this.deckNameCtrl.value;
        this.decksService.update((this.deck as any).id, {
            name: this.deckNameCtrl.value
        })
            .subscribe(deck => this.deck.name = deck.name);
    }

    public saveFormat() {
        this.editFormat = false;
        this.deck.format = this.deckFormatCtrl.value;
        this.decksService.update((this.deck as any).id, {
            format: this.deckFormatCtrl.value
        })
            .subscribe(deck => this.forceUpdate());
    }

    public saveFocus() {
        this.editFocus = false;
        this.decksService.update((this.deck as any).id, {
            specialData: this.deck.specialData
        })
            .subscribe(deck => this.forceUpdate());
    }

    public save() {
        this.fullEditMode = false;
        this.decksService.update((this.deck as any).id, {
            name: this.deckNameCtrl.value,
            format: this.deckFormatCtrl.value,
            specialData: this.deck.specialData,
            isPublic: this.isPublicCtrl.value,
            isLinkShareable: this.isLinkShareableCtrl.value
        })
            .pipe(flatMap((deck: Deck) => {
                this.gtag.event('deck_updated', {
                    deck_id: deck.id,
                    user_id: this.authService.currentUserId,
                    event_category: 'decks'
                });
                return this.decksService.get(deck.id, this.deckQuery(this.queryUpdated.value), Utils.PageOrDefault(this.pageUpdated.value));
            }))
            .subscribe(this.handleDeckUpdate.bind(this));
    }

    public selectFocused() {
        this.format.addFocused().subscribe((event: AddFocusEvent) => {
            this.deck.specialData = event.specialData;
            this.gtag.event('focus_added_to_deck', {
                card_id: event.card.id,
                deck_id: this.deck.id,
                deck_format: this.deck.format,
                user_id: this.authService.currentUserId,
                event_category: 'decks'
            });
            this.focusedCard.next(event.card);
            this.decksService.get(this.deck.id, {
                dataField: {dataType: 'string', title: 'Name', field: 'name'},
                operation: '=',
                value: event.card.name
            }).pipe(
                filter(deck => deck.cards.length === 0),
                flatMap(() => this.decksService.addCard(this.deck.id, event.card.id))
            ).subscribe();
            this.setAddCardsQuery();
        });
    }

    public onCardDrop(dropData: CdkDragDrop<Card[]>) {
        this.addCard(dropData.item.data);
    }

    public forceUpdate() {
        if (!this.deck) {
            return;
        }
        this.decksService.get(this.deck.id, this.deckQuery(this.queryUpdated.value), Utils.PageOrDefault(this.pageUpdated.value))
            .subscribe(this.handleDeckUpdate.bind(this));
    }

    public copyShareLink() {
        const shareLinkLocation = `${location.origin}/decks/share/${this.deck.id}`;
        this.clipboard.copy(shareLinkLocation);
        this.alertsService.alert('Copied!', AlertLevel.InfoSuccess);
    }

    public setLikeIconName() {
        if (!this.currentUser || !this.deck || this.currentUser.upvotedDeckIds.indexOf(this.deck.id) === -1) {
            this.likeIconName = 'favorite_border';
        } else {
            this.likeIconName = 'favorite';
        }
    }

    public toggleLikeDeck() {
        const updateForDeckLikes = () => {
            this.updateCurrentUser();
            this.forceUpdate();
        };

        if (this.currentUser.upvotedDeckIds.indexOf(this.deck.id) !== -1) {
            this.decksService.removeUpvoteFromDeck(this.deck.id, this.currentUser.id).subscribe(updateForDeckLikes);
        } else {
            this.decksService.addUpvoteToDeck(this.deck.id, this.currentUser.id).subscribe(updateForDeckLikes);
        }
    }

    public showManualImport() {
        const importer = this.dialog.open(ManualImporterComponent, {width: '100vw', height: '100vh'});
        importer.componentInstance.mode = 'deck';
        importer.afterClosed().subscribe(this.onImportDialogClosed.bind(this));
    }

    public showFileImport() {
        const importer = this.dialog.open(FileImporterComponent, {width: '100vw', height: '100vh'});
        importer.componentInstance.mode = 'deck';
        importer.afterClosed().subscribe(this.onImportDialogClosed.bind(this));
    }

    public showInventoryMatchDialog(selectedCard: CardAndRelation, markedOwned: boolean) {
        const data: InventoryCardDialogData = {
            deckCard: selectedCard,
            deckId: this.deck.id,
            markedOwned
        };
        const inventoryCardDialog = this.dialog.open(InventoryCardDialogComponent, { data, minWidth: '600px'});
        // Update the view of the deck to match the modifications
        merge(
            inventoryCardDialog.componentInstance.deckChanged,
            inventoryCardDialog.backdropClick(),
            inventoryCardDialog.keydownEvents().pipe(filter((value, index) => value.key === 'Escape'))
        )
            .pipe(
                switchMap(() =>
                    // We need to make sure that 'owned' checkboxes go back to their correct state after the user has clicked 'cancel'. We also need to
                    // update if they changed something. Therefore, we're going to kick off this deck query regardless of if they actually made changes or not,
                    // since otherwise we have to do some very complicated logic to reach down into the underlying SimpleTableComponent to set the checkbox
                    // back to the correct state.
                    this.decksService.get(this.deck.id, this.deckQuery(this.queryUpdated.value), Utils.PageOrDefault(this.pageUpdated.value))
                )
            ).subscribe(this.handleDeckUpdate.bind(this));
    }

    private onImportDialogClosed(cards) {
        if (!cards) {
            return;
        }

        cards.map(card => ({addFromInventory: card.addFromInventory, importIntoInventory: card.importIntoInventory, quantityInDeck: card.qty, ...card.card}))
            .forEach(cardAndAdd => {
                const {addFromInventory, importIntoInventory, ...card} = cardAndAdd;
                // To add a card from our inventory, we must have the correct relation id
                // This requires that we look up the actual inventory card
                // If we cannot find it, fall back to the regular card and alert the user
                if (addFromInventory || importIntoInventory) {
                    this.inventoryService.getInventory({
                        query: {
                            dataField: {field: 'id', title: '', dataType: 'string'} as any,
                            operation: '=',
                            value: card.id
                        },
                        pagination: {
                            page: {pageIndex: 0, pageSize: 10},
                            sort: {active: 'released_at', direction: 'desc'}
                        }
                    }).subscribe(cardsAndCount => {
                        if (cardsAndCount.totalCount > 0 && addFromInventory) {
                            this.addCard({quantityInDeck: card.quantityInDeck, ...cardsAndCount.inventoryCards[0]} as any);
                        } else if (cardsAndCount.totalCount === 0 && importIntoInventory) {
                            this.inventoryService.addToInventory([{quantity: card.quantityInDeck, ...card}]).subscribe(addedCards => {
                                this.addCard({quantityInDeck: card.quantityInDeck, ...addedCards[0]} as any);
                            });
                        } else {
                            this.addCard(card);
                            this.alertsService.alert(`We could not find a card ${card.name} (${card.setName}) in your inventory, so we added an un-owned instance to the deck.`);
                        }
                    });
                } else {
                    this.addCard(card);
                }
            });
    }

    public onCardInputChanged(cardInputChangedEvent: { value: any; data: any; dataField: EditableDataField }) {
        const {value, data, dataField} = cardInputChangedEvent;
        if (dataField.field === 'quantityInDeck') {
            this.decksService.setQuantity(this.deck.id, data.relationToDeckId, value)
                .pipe(flatMap((deck: Deck) => {
                    this.refreshCharts = true;
                    this.gtag.event('card_qty_updated_for_deck', {
                        card_id: data.id,
                        user_id: this.authService.currentUserId,
                        deck_id: deck.id,
                        deck_format: deck.format,
                        old_value: data.quantityInDeck,
                        new_value: value,
                        event_category: 'decks'
                    });
                    return this.decksService.get(deck.id, this.deckQuery(this.queryUpdated.value), Utils.PageOrDefault(this.pageUpdated.value));
                }))
                .subscribe(this.handleDeckUpdate.bind(this));
        } else if (dataField.field === 'inventoryId') {
            // Kick off the inventory chooser dialog
            this.showInventoryMatchDialog(data, value);
        }
    }

    public doExport(toFile = false) {
        this.decksService.createExport(this.deck.id).subscribe(({exportString}) => {
            Utils.handleExport(exportString, this.clipboard, this.alertsService, toFile ? `${this.deck.name} Export` : '');
        });
    }

}
